import React, { useEffect, useState } from "react";
import { Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Activity,
  Crown,
  Clock3,
  Medal,
  Radio,
  ShieldCheck,
  Swords,
  Table2,
  Trophy,
  X,
} from "lucide-react";
import { AuthProvider } from "./hooks/useAuth.jsx";
import ProtectedRoute from "./components/common/ProtectedRoute";
import ErrorBoundary from "./components/common/ErrorBoundary";
import Loader from "./components/common/Loader";
import { API_BASE, apiGet } from "./utils/api";
import "./App.css";

import LoginPage from "./pages/LoginPage";
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const GamePage = lazy(() => import("./pages/GamePage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const TournamentPage = lazy(() => import("./pages/TournamentPage"));
const UserJourney = lazy(() => import("./components/game/UserJourney"));

const FallbackBlock = ({ className = "" }) => (
  <div
    className={`shimmer rounded-lg bg-slate-700/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ${className}`}
  />
);

function TournamentRouteFallback() {
  return (
    <div
      className="min-h-screen bg-cover bg-center px-4 pb-10 pt-24 text-slate-100 sm:px-6 lg:px-8"
      style={{ backgroundImage: "url('https://asset.langprotect.com/lp-game/game-bg.webp')" }}
    >
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-[1.4rem] border border-indigo-500/10 bg-[#070b1d]/75 px-5 py-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)] sm:px-8 lg:px-10">
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_78%_40%,rgba(168,85,247,0.18),transparent_26%),linear-gradient(90deg,rgba(7,11,29,0.98)_0%,rgba(7,11,29,0.84)_56%,rgba(15,23,42,0.48)_100%)]"
            aria-hidden="true"
          />
          <div className="relative max-w-2xl">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-purple-300">
              <Radio className="h-4 w-4" />
              Tournament Roof
            </p>
            <FallbackBlock className="mt-4 h-12 w-80 max-w-full" />
            <FallbackBlock className="mt-5 h-4 w-[560px] max-w-full" />
            <FallbackBlock className="mt-2 h-4 w-96 max-w-full" />
          </div>
        </section>

        <section className="rounded-[1.35rem] border border-indigo-400/20 bg-[#0b1027]/90 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.36),inset_0_1px_0_rgba(255,255,255,0.06)] sm:p-5">
          <div className="grid gap-3 rounded-2xl border border-slate-800/80 bg-[#070b1d]/80 p-2 sm:grid-cols-2">
            <div className="rounded-xl border border-violet-400/30 bg-violet-500/10 px-4 py-4">
              <span className="flex items-center gap-3 text-sm font-black text-slate-100">
                <Trophy className="h-5 w-5 text-violet-300" />
                <FallbackBlock className="h-4 w-32" />
              </span>
              <FallbackBlock className="mt-3 h-3 w-56 max-w-full" />
            </div>
            <div className="rounded-xl border border-transparent px-4 py-4">
              <span className="flex items-center gap-3 text-sm font-black text-slate-400">
                <Swords className="h-5 w-5 text-cyan-300" />
                <FallbackBlock className="h-4 w-32" />
              </span>
              <FallbackBlock className="mt-3 h-3 w-56 max-w-full" />
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="rounded-[1.35rem] border border-indigo-400/20 bg-[#0b1027]/90 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.06)] sm:p-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-violet-300">
                  <Trophy className="h-4 w-4" />
                  Create mode
                </p>
                <FallbackBlock className="mt-3 h-7 w-48" />
                <FallbackBlock className="mt-4 h-4 w-[520px] max-w-full" />
                <FallbackBlock className="mt-2 h-4 w-80 max-w-full" />
              </div>
              <div className="rounded-xl border border-slate-700/60 bg-[#080d22]/75 px-4 py-3">
                <FallbackBlock className="h-4 w-28" />
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={index}
                  className="rounded-2xl border border-slate-700/60 bg-[#080d22]/75 p-4"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-600/40 bg-slate-900/60 text-slate-400">
                        <Clock3 className="h-5 w-5" />
                      </span>
                      <div>
                        <FallbackBlock className="h-4 w-28" />
                        <FallbackBlock className="mt-2 h-3 w-20" />
                      </div>
                    </div>
                    <FallbackBlock className="h-3 w-10" />
                  </div>
                  <div className="mt-4 grid grid-cols-5 gap-2">
                    {Array.from({ length: 5 }).map((__, optionIndex) => (
                      <FallbackBlock
                        key={optionIndex}
                        className="h-10 rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-slate-700/60 bg-[#080d22]/75 p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <FallbackBlock className="h-10 w-10 rounded-xl" />
                  <div>
                    <FallbackBlock className="h-4 w-24" />
                    <FallbackBlock className="mt-2 h-3 w-44" />
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:min-w-96">
                  <FallbackBlock className="h-[66px] rounded-lg" />
                  <FallbackBlock className="h-[66px] rounded-lg" />
                </div>
              </div>
            </div>

            <FallbackBlock className="mt-5 h-12 w-full rounded-xl sm:w-72" />
          </div>

          <aside className="rounded-[1.35rem] border border-indigo-400/20 bg-[#0b1027]/90 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.06)] sm:p-6">
            <FallbackBlock className="h-6 w-32" />
            <FallbackBlock className="mt-4 h-4 w-64 max-w-full" />
            <FallbackBlock className="mt-2 h-4 w-48 max-w-full" />
            <div className="mt-5 rounded-2xl border border-slate-700/60 bg-[#080d22]/75 p-4">
              <FallbackBlock className="h-3 w-24" />
              <div className="mt-4 flex items-center gap-3">
                <FallbackBlock className="h-10 w-10 rounded-full" />
                <FallbackBlock className="h-4 flex-1" />
              </div>
            </div>
            <div className="mt-5 space-y-4">
              <FallbackBlock className="h-4 w-full" />
              <FallbackBlock className="h-4 w-4/5" />
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <div key={location.pathname} className="route-fade">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game"
          element={
            <ProtectedRoute>
              <GamePage />
            </ProtectedRoute>
          }
        />
        <Route path="/rooms" element={<Navigate to="/tournament" replace />} />
        <Route
          path="/rooms/:codename/replay"
          element={
            <ProtectedRoute>
              <TournamentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <LeaderboardPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournament"
          element={
            <ProtectedRoute>
              <TournamentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournament/:codename/join"
          element={
            <ProtectedRoute authMode="register">
              <TournamentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournament/:codename/replay"
          element={
            <ProtectedRoute>
              <TournamentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journey"
          element={
            <ProtectedRoute>
              <UserJourney />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  );
}

function TournamentGameOverOverlay({ payload, onClose }) {
  const room = payload?.room || payload;
  const summary = room?.end_summary || {};
  const players =
    room?.leaderboard?.filter((row) =>
      ["player", "eliminated"].includes(row.role),
    ) || [];
  const winner = summary.winner || players[0];
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("ai_escape_game_user") || "null");
    } catch {
      return null;
    }
  })();
  const viewerFromLeaderboard = players.find(
    (row) => row.username === storedUser?.username,
  );
  const viewer = viewerFromLeaderboard || summary.viewer || {};
  const moments = summary.key_moments || [];
  const topPlayers = players.slice(0, 3);
  const hasClear = Boolean(summary.has_clear);
  const isZeroScoreNoClear = summary.outcome_state === "no_clears_zero_score";
  const viewerRank = viewer.rank || null;
  const playerCount = players.length || summary.player_count || 0;
  const viewerPercentile =
    viewerRank && playerCount >= 10
      ? Math.round(((playerCount - viewerRank + 1) / playerCount) * 100)
      : viewer.percentile;
  const viewerStanding = viewerPercentile
    ? `Top ${viewerPercentile}%`
    : viewerRank
      ? `#${viewerRank} of ${playerCount || "-"}`
      : "Unranked";
  const nextRank =
    viewerRank && viewerRank > 1
      ? players.find((row) => row.rank === viewerRank - 1)
      : null;
  const deltaFromNextRank =
    viewer.delta_from_next_rank ??
    (nextRank ? Math.max(0, nextRank.score - (viewer.score || 0)) : null);
  const viewerTitle = viewerRank
    ? `You placed #${viewerRank}`
    : hasClear
      ? "Tournament concluded"
      : "No levels cleared";
  const viewerSubtitle = viewerRank ? (
    <>
      {viewer.username || "You"} finished with{" "}
      <span className="font-bold text-cyan-100">{viewer.score || 0}</span>{" "}
      points. Winner: {winner?.username || "unclaimed"}
      {winner?.score !== undefined ? ` (${winner.score} pts).` : "."}
    </>
  ) : isZeroScoreNoClear ? (
    "The cutoff hit before anyone could convert progress into score."
  ) : (
    `${winner?.username || "Top rank"} held the lead at cutoff.`
  );
  const podiumStyles = {
    1: {
      row: "border-amber-300/40 bg-amber-300/12 shadow-[0_0_24px_oklch(0.82_0.12_80_/_0.12)]",
      badge: "bg-amber-300/18 text-amber-100 ring-1 ring-amber-200/30",
      icon: "text-amber-200",
    },
    2: {
      row: "border-slate-300/35 bg-slate-200/10 shadow-[0_0_20px_oklch(0.82_0.02_260_/_0.08)]",
      badge: "bg-slate-200/14 text-slate-100 ring-1 ring-slate-200/25",
      icon: "text-slate-200",
    },
    3: {
      row: "border-orange-300/35 bg-orange-300/10 shadow-[0_0_20px_oklch(0.72_0.12_55_/_0.1)]",
      badge: "bg-orange-300/14 text-orange-100 ring-1 ring-orange-200/25",
      icon: "text-orange-200",
    },
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] h-dvh w-screen overflow-y-auto bg-[oklch(0.09_0.025_277)] text-slate-100">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_18%_14%,oklch(0.62_0.16_215_/_0.22),transparent_30%),radial-gradient(circle_at_82%_4%,oklch(0.68_0.16_325_/_0.18),transparent_28%),linear-gradient(180deg,oklch(0.11_0.03_277_/_0.88),oklch(0.075_0.02_277))]" />
      <div className="pointer-events-none fixed inset-0 opacity-[0.08] [background-image:linear-gradient(oklch(0.72_0.12_215)_1px,transparent_1px),linear-gradient(90deg,oklch(0.72_0.12_215)_1px,transparent_1px)] [background-size:44px_44px]" />

      <button
        type="button"
        aria-label="Close tournament results"
        onClick={onClose}
        className="fixed right-4 top-4 z-[10000] inline-flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-300/25 bg-slate-950/90 text-cyan-100 shadow-xl shadow-cyan-950/30 transition-colors hover:border-cyan-200/50 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-300"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative min-h-dvh px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto w-full max-w-7xl space-y-5">
          <section className="overflow-hidden rounded-2xl border border-cyan-300/20 bg-[oklch(0.14_0.035_277_/_0.92)] shadow-2xl shadow-cyan-950/30">
            <div className="grid gap-0 lg:grid-cols-[1fr_360px]">
              <div className="p-6 sm:p-8 lg:p-10">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200/80">
                  <Trophy
                    className={`h-4 w-4 ${hasClear ? "text-amber-200" : "text-slate-500"}`}
                  />
                  Tournament concluded
                </p>
                <div className="mt-5 max-w-3xl">
                  <h1 className="text-4xl font-black tracking-tight text-slate-50 sm:text-6xl">
                    {viewerTitle}
                  </h1>
                  <p className="mt-3 max-w-2xl text-base font-medium leading-7 text-slate-300 sm:text-lg">
                    {viewerSubtitle}
                  </p>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-cyan-300/15 bg-cyan-300/5 p-4">
                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200/70">
                      <ShieldCheck className="h-4 w-4" /> Clear state
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-50">
                      {hasClear ? "Key secured" : "Vault held"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-purple-300/15 bg-purple-300/5 p-4">
                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-purple-200/75">
                      <Activity className="h-4 w-4" /> Your standing
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-50">
                      {viewerStanding}
                    </p>
                  </div>
                </div>
              </div>

              <aside className="border-t border-cyan-300/15 bg-slate-950/35 p-6 lg:border-l lg:border-t-0 lg:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Final signal
                </p>
                <div className="mt-5 flex items-center gap-4">
                  <div className="winner-crown-box grid h-16 w-16 place-items-center rounded-2xl border border-amber-200/35 bg-amber-300/10 text-amber-100 shadow-lg shadow-amber-950/30">
                    <Crown className="winner-crown-icon h-8 w-8" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Top operator</p>
                    <p className="text-2xl font-black text-slate-50">
                      {winner?.username || "Unclaimed"}
                    </p>
                  </div>
                </div>
                <div className="mt-6 space-y-2">
                  {topPlayers.length > 0 ? (
                    topPlayers.map((row) => {
                      const style = podiumStyles[row.rank] || podiumStyles[1];
                      return (
                        <div
                          key={row.participant_id}
                          className={`flex items-center justify-between rounded-xl border px-4 py-3 ${style.row}`}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`grid h-8 w-8 place-items-center rounded-lg text-sm font-black ${style.badge}`}
                            >
                              #{row.rank}
                            </span>
                            <span className="font-semibold text-slate-100">
                              {row.username}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-slate-200">
                            {row.score}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <p className="rounded-xl border border-slate-700/70 bg-slate-950/45 px-4 py-3 text-sm text-slate-400">
                      No player results were returned.
                    </p>
                  )}
                </div>
              </aside>
            </div>
          </section>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
            <section className="rounded-2xl border border-cyan-300/20 bg-[oklch(0.14_0.035_277_/_0.88)] p-5 shadow-[inset_0_1px_0_oklch(0.72_0.12_215_/_0.16)]">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-50">
                  <Table2 className="h-4 w-4 text-cyan-200" />
                  Final leaderboard
                </h2>
                {summary.tiebreak_summary && (
                  <p className="rounded-lg border border-amber-300/25 bg-amber-300/10 px-3 py-2 text-sm text-amber-100">
                    {summary.tiebreak_summary}
                  </p>
                )}
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-700/70">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="bg-slate-950/70 text-xs uppercase tracking-[0.14em] text-slate-400">
                      <tr>
                        <th className="px-4 py-3">Rank</th>
                        <th className="px-4 py-3">Handle</th>
                        <th className="px-4 py-3">Final score</th>
                        <th className="px-4 py-3">Levels cleared</th>
                        <th className="px-4 py-3">Finish time</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/90 bg-slate-950/25 text-slate-200">
                      {players.map((row) => {
                        const isViewer = row.rank === viewerRank;
                        const podiumClass =
                          row.rank === 1
                            ? "bg-amber-300/10 text-amber-50"
                            : row.rank === 2
                              ? "bg-slate-200/8 text-slate-50"
                              : row.rank === 3
                                ? "bg-orange-300/8 text-orange-50"
                                : "hover:bg-cyan-300/5";
                        return (
                          <tr
                            key={row.participant_id}
                            className={
                              isViewer
                                ? `${podiumClass} ring-1 ring-inset ring-purple-300/40`
                                : podiumClass
                            }
                          >
                            <td className="px-4 py-3 font-black text-slate-50">
                              #{row.rank}
                            </td>
                            <td className="px-4 py-3 font-semibold">
                              {row.username}
                            </td>
                            <td className="px-4 py-3 font-bold text-cyan-100">
                              {row.score}
                            </td>
                            <td className="px-4 py-3">{row.levels_cleared}</td>
                            <td className="px-4 py-3">
                              {row.last_level_time_seconds !== null &&
                              row.last_level_time_seconds !== undefined
                                ? `${row.last_level_time_seconds}s`
                                : "Did not finish"}
                            </td>
                            <td
                              className={`px-4 py-3 ${row.disconnected ? "text-red-200" : "text-slate-300"}`}
                            >
                              {row.disconnected
                                ? "Disconnected"
                                : row.status_label || row.final_note || "Ended"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <div className="space-y-5">
              <section className="rounded-2xl border border-purple-300/20 bg-[oklch(0.14_0.035_277_/_0.88)] p-5 shadow-[inset_0_1px_0_oklch(0.76_0.13_315_/_0.14)]">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-purple-200/80">
                  Your result
                </p>
                <p className="mt-3 text-4xl font-black text-slate-50">
                  #{viewerRank || "-"}
                </p>
                <p className="mt-2 text-sm text-slate-300">
                  {viewer.score || 0} points, {viewerStanding}
                </p>
                {deltaFromNextRank !== null &&
                  deltaFromNextRank !== undefined &&
                  nextRank && (
                    <p className="mt-4 rounded-lg border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-sm text-amber-100">
                      {deltaFromNextRank} points from #{nextRank.rank}
                    </p>
                  )}
              </section>

              <section className="rounded-2xl border border-amber-300/20 bg-[oklch(0.14_0.035_277_/_0.88)] p-5 shadow-[inset_0_1px_0_oklch(0.82_0.12_80_/_0.14)]">
                <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-50">
                  <Medal className="h-4 w-4 text-amber-200" />
                  Key moments
                </h2>
                <div className="space-y-3">
                  {moments.length > 0 ? (
                    moments.map((moment) => (
                      <div
                        key={moment.label}
                        className="rounded-xl border border-slate-700/70 bg-slate-950/35 p-3"
                      >
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                          {moment.label}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-50">
                          {moment.value || "Unclaimed"}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-400">
                          {moment.detail}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="rounded-xl border border-slate-700/70 bg-slate-950/35 p-3 text-sm text-slate-400">
                      No decisive moments were recorded.
                    </p>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isGameOverVisible, setIsGameOverVisible] = useState(false);
  const [gameOverPayload, setGameOverPayload] = useState(null);
  const [activeRoomCode, setActiveRoomCode] = useState(
    () => localStorage.getItem("langprotect_last_room") || "",
  );

  const closeGameOver = () => {
    setIsGameOverVisible(false);
    setGameOverPayload(null);
    localStorage.removeItem("langprotect_last_room");
    setActiveRoomCode("");
    navigate("/dashboard", { replace: true });
  };

  useEffect(() => {
    const handleTournamentEnded = (event) => {
      setGameOverPayload(event.detail);
      setIsGameOverVisible(true);
    };
    const handleRoomSelected = (event) => {
      setActiveRoomCode(event.detail?.codename || "");
    };
    const handleStorage = (event) => {
      if (event.key === "langprotect_last_room")
        setActiveRoomCode(event.newValue || "");
    };

    window.addEventListener(
      "langprotect:tournament-ended",
      handleTournamentEnded,
    );
    window.addEventListener("langprotect:room-selected", handleRoomSelected);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(
        "langprotect:tournament-ended",
        handleTournamentEnded,
      );
      window.removeEventListener(
        "langprotect:room-selected",
        handleRoomSelected,
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!activeRoomCode || !API_BASE) return undefined;
    const wsBase = API_BASE.replace(/^http/, "ws");
    const socket = new WebSocket(`${wsBase}/rooms/${activeRoomCode}/ws`);

    socket.onmessage = async (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type !== "TOURNAMENT_ENDED") return;
        const roomRes = await apiGet(`/rooms/${activeRoomCode}`, false);
        const room = roomRes.ok ? await roomRes.json() : null;
        setGameOverPayload({ ...payload, room });
        setIsGameOverVisible(true);
      } catch {
        setGameOverPayload({
          type: "TOURNAMENT_ENDED",
          codename: activeRoomCode,
        });
        setIsGameOverVisible(true);
      }
    };

    return () => socket.close();
  }, [activeRoomCode]);

  const isTournamentRoute =
    location.pathname.startsWith("/tournament") ||
    location.pathname.startsWith("/rooms");
  const routeFallback = isTournamentRoute ? <TournamentRouteFallback /> : <Loader />;

  return (
    <div className="App">
      <Suspense fallback={routeFallback}>
        <AnimatedRoutes />
      </Suspense>
      {isGameOverVisible && (
        <TournamentGameOverOverlay
          payload={gameOverPayload}
          onClose={closeGameOver}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

export default App;
